import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import type { DailyUsage, Plan } from '@vibe/shared-types'

export interface WeeklyUsageInfo {
  weekId: string
  tokensBilled: number
  tokensCap: number
  msUntilReset: number
}

interface UsageResponse {
  usage: DailyUsage
  limits: {
    chat: number
    upper: number
    opus: number
    db_write: number
    db_read: number
    deploy: number
    tool_call: number
  }
  plan: Plan
  /** plan v1.3 §16 PR #12 — 주별 토큰 한도 + 남은 일수. */
  weekly?: WeeklyUsageInfo
}

const REFRESH_INTERVAL_MS = 30 * 1000

/**
 * useUsage — `/api/usage/today` 를 30초마다 polling.
 *
 * UsagePill의 percent 계산은 chat 카운터 기준 (가장 자주 변하고 학생이 가장 빨리 체감).
 * 학생이 챗 메시지 보내면 backend가 즉시 KV에 +1 하므로 다음 polling 사이클에 반영됨.
 *
 * idToken 이 store 에 채워질 때까지 fetch 보류 — AuthBootstrap 비동기 콜백과의
 * race condition 으로 401 NO_AUTH 가 콘솔에 잡히는 문제를 막는다.
 * queryKey 에 idToken 존재 여부를 포함해 토큰 변화 시 자동 재실행.
 */
export function useUsage() {
  const idToken = useAuthStore((s) => s.idToken)
  return useQuery({
    queryKey: ['usage', 'today', !!idToken],
    queryFn: () => apiFetch<UsageResponse>('/api/usage/today'),
    enabled: !!idToken,
    refetchInterval: REFRESH_INTERVAL_MS,
    staleTime: 10 * 1000,
    retry: (count, err) => {
      // 401 (인증 안 됨) 은 polling 재시도 의미 없음
      if (typeof err === 'object' && err && 'status' in err && err.status === 401) return false
      return count < 2
    },
  })
}

/**
 * chat 한도 대비 사용률을 0~100 사이로.
 * limits.chat 이 0이면 (free 플랜 등) 그대로 0 반환.
 */
export function selectChatPercent(data: UsageResponse | undefined): number {
  if (!data) return 0
  const limit = data.limits.chat
  if (limit <= 0) return 0
  const used = data.usage.chat ?? 0
  return Math.min(100, Math.round((used / limit) * 100))
}

/**
 * plan v1.3 §16 — 주별 토큰 한도 대비 사용률.
 * basic 200K / premium 500K. free 는 cap 0 이라 표시 안 함.
 */
export function selectWeeklyPercent(data: UsageResponse | undefined): number {
  const w = data?.weekly
  if (!w || w.tokensCap <= 0) return 0
  return Math.min(100, Math.round((w.tokensBilled / w.tokensCap) * 100))
}

/**
 * "다음 초기화: 일요일 자정" 표시. 일 수가 1 이하면 "오늘" 또는 "내일".
 */
export function formatWeeklyResetLabel(ms: number): string {
  if (ms <= 0) return '곧 초기화'
  const days = Math.floor(ms / (24 * 60 * 60 * 1000))
  if (days === 0) return '오늘 자정 초기화'
  if (days === 1) return '내일 초기화'
  return `${days}일 후 초기화`
}
